import type { OptimizerAuditV2Record } from '../../types/audit.types';

export const REBALANCE_MODES = [
  'DEPLOY_CASH',
  'RAISE_CASH',
  'FULL_REBALANCE',
] as const;

export const SCOPED_CASH_EXECUTION_MODES = [
  'DEPLOY_CASH',
  'RAISE_CASH',
  'SCOPED_CASH',
] as const;

export type RebalanceModeV2 = (typeof REBALANCE_MODES)[number];

export type ScopedCashExecutionMode =
  (typeof SCOPED_CASH_EXECUTION_MODES)[number];

export const isScopedCashExecutionMode = (
  mode: string,
): mode is ScopedCashExecutionMode =>
  (SCOPED_CASH_EXECUTION_MODES as readonly string[]).includes(mode);

export const isScopedCashExecution = (
  audit: OptimizerAuditV2Record | null | undefined,
): boolean => {
  const mode = audit?.effectiveRebalanceMode ?? audit?.rebalanceMode;
  return mode != null && isScopedCashExecutionMode(mode);
};

export const isKnownRebalanceMode = (mode: string): mode is RebalanceModeV2 =>
  (REBALANCE_MODES as readonly string[]).includes(mode);

/** Prefer executed mode from HS; fall back to requested mode when absent. */
export const resolveEffectiveRebalanceMode = (
  audit: OptimizerAuditV2Record | null | undefined,
): RebalanceModeV2 | null => {
  const mode = audit?.effectiveRebalanceMode ?? audit?.rebalanceMode;
  if (!mode || !isKnownRebalanceMode(mode)) {
    return null;
  }

  return mode;
};

/** Mode badge is for executed optimizers only — not validation failures. */
export const resolveRebalanceModeForDisplay = (
  audit: OptimizerAuditV2Record | null | undefined,
): RebalanceModeV2 | null => {
  if (audit?.passed === false) {
    return null;
  }

  return resolveEffectiveRebalanceMode(audit);
};

export const getRebalanceModeBadgeColor = (
  mode: RebalanceModeV2,
): 'positive' | 'warning' | 'supportOne' => {
  switch (mode) {
    case 'DEPLOY_CASH':
      return 'positive';
    case 'RAISE_CASH':
      return 'warning';
    case 'FULL_REBALANCE':
      return 'supportOne';
    default:
      return 'supportOne';
  }
};

export const getRebalanceModeLabelKey = (mode: RebalanceModeV2): string =>
  `rebalanceMode.${mode}`;
